import {
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import {
    MAT_DIALOG_DEFAULT_OPTIONS,
    MatDialogConfig,
} from '@angular/material/dialog';
import {
    MAT_FORM_FIELD_DEFAULT_OPTIONS,
    MatFormFieldDefaultOptions,
} from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { provideQueryAwareScrolling } from './core/router/router-scroll';
import { SocketService } from './core/socket/socket.service';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { UnreadCountService } from './core/whatsapp/unread-count.service';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            // Positions are still recorded; `provideQueryAwareScrolling` decides what
            // to do with them, so a query-string-only navigation keeps the scroll.
            withInMemoryScrolling({ scrollPositionRestoration: 'disabled' })
        ),
        provideQueryAwareScrolling(),

        // Material form fields. One place decides how every field in the app
        // behaves, instead of each template remembering the attributes: without
        // `dynamic`, Material reserves a fixed-height subscript strip under the
        // control, which reads as a phantom block of empty space glued to it.
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic',
            } as MatFormFieldDefaultOptions,
        },

        // Material dialogs. Material's default is 'first-tabbable', and in every
        // dialog here the first tabbable element is the X in the header — so
        // opening one landed a focus ring on "close", which reads as the action
        // being offered. 'dialog' focuses the container instead: nothing is
        // highlighted, and focus is still inside the dialog for the keyboard and
        // for screen readers, which 'false' would not give.
        //
        // A dialog whose point is typing can still claim the caret by putting
        // cdkFocusInitial on its own control.
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                autoFocus: 'dialog',
            } as MatDialogConfig,
        },

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'es',
                        label: 'Español',
                    },
                    {
                        id: 'en',
                        label: 'English',
                    },
                ],
                defaultLang: 'es',
                fallbackLang: 'es',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const availableLangs = ['es', 'en'];

            // Check localStorage for saved language preference
            const savedLang = localStorage.getItem('selectedLanguage');
            const langToUse =
                savedLang && availableLangs.includes(savedLang)
                    ? savedLang
                    : translocoService.getDefaultLang();

            translocoService.setActiveLang(langToUse);

            return firstValueFrom(translocoService.load(langToUse));
        }),

        // Initialize Socket Service
        provideAppInitializer(() => {
            inject(SocketService);
        }),

        // Initialize Unread Count Service (listens to websocket for unread message notifications)
        provideAppInitializer(() => {
            inject(UnreadCountService);
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'centered',
                scheme: 'dark',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-teal',
                themes: [
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
